import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";

// Search-as-you-type address suggestions (OpenStreetMap Nominatim via backend).
export function AddressAutocomplete({ value, onChange, disabled, testid = "address-autocomplete" }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const skipRef = useRef(false);

  useEffect(() => {
    if (skipRef.current) { skipRef.current = false; return; }
    const q = (value || "").trim();
    if (q.length < 3) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try { const data = await api.addressSearch(q); setResults(data.results || []); setOpen(true); }
      catch { setResults([]); }
      finally { setLoading(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (label) => { skipRef.current = true; onChange(label); setOpen(false); setResults([]); };

  return (
    <div className="relative flex-1" ref={boxRef}>
      <div className="relative">
        <Input data-testid={testid} value={value} disabled={disabled} autoComplete="off"
          onChange={(e) => onChange(e.target.value)} onFocus={() => results.length && setOpen(true)}
          className="h-9 text-xs pr-7" placeholder="Start typing an address…" />
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />}
      </div>
      {open && results.length > 0 && (
        <div data-testid={`${testid}-list`} className="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {results.map((r, i) => (
            <button type="button" key={i} data-testid={`${testid}-opt-${i}`} onClick={() => pick(r.label)}
              className="flex w-full items-start gap-2 px-3 py-2 text-left text-[11px] hover:bg-slate-50 border-b border-slate-50 last:border-0">
              <MapPin className="h-3 w-3 mt-0.5 text-brand shrink-0" />
              <span className="text-slate-700">{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
